import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { enUS, frFR, nlBE } from '@clerk/localizations';
import en from './locales/en';
import fr from './locales/fr';
import nl from './locales/nl';

export const SUPPORTED_LANGUAGES = ['en', 'fr', 'nl'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

const STORAGE_KEY = 'squads.lang';

const toSupported = (value: string | null | undefined): Language | null => {
  const code = value?.slice(0, 2).toLowerCase();
  return SUPPORTED_LANGUAGES.find(l => l === code) ?? null;
};

// The host page passes ?lang=; a remembered choice and the browser follow.
const detectLanguage = (): Language => {
  const fromUrl = toSupported(new URLSearchParams(window.location.search).get('lang'));
  if (fromUrl) {
    try {
      localStorage.setItem(STORAGE_KEY, fromUrl);
    } catch {
      // Storage can be unavailable in private windows; the URL value still applies.
    }
    return fromUrl;
  }
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch {
    stored = null;
  }
  return toSupported(stored) ?? toSupported(navigator.language) ?? 'en';
};
/** The in-app switch: remembered like a URL choice, applied at once. */
export const setLanguage = (lng: Language) => {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    // Private windows: the choice still applies for this visit.
  }
  void i18n.changeLanguage(lng);
};

i18n.use(initReactI18next).init({
  lng: detectLanguage(),
  fallbackLng: 'en',
  supportedLngs: [...SUPPORTED_LANGUAGES],
  resources: {
    en: { translation: en },
    fr: { translation: fr },
    nl: { translation: nl },
  },
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

document.documentElement.lang = i18n.language;
i18n.on('languageChanged', lng => {
  document.documentElement.lang = lng;
});

const CLERK_LOCALIZATIONS: Record<Language, typeof enUS> = { en: enUS, fr: frFR, nl: nlBE };

export const clerkLocalization = () => CLERK_LOCALIZATIONS[toSupported(i18n.language) ?? 'en'];

export default i18n;
